import { apiFetch } from '@/lib/api';

export interface FlightManifestDTO {
	id: string;
	organization_id: string;
	event_id: string;
	name: string;
	status: 'uploading' | 'processing' | 'review' | 'approved' | 'failed';
	destination_airports?: string[] | null;
	created_by: string | null;
	created_at: string;
	updated_at: string;
}

export interface FlightManifestFileDTO {
	id: string;
	manifest_id: string;
	original_filename: string;
	r2_key: string;
	file_url: string;
	file_size: number;
	status: 'uploaded' | 'processing' | 'extracted' | 'failed';
	error_message: string | null;
}

export interface FlightManifestRecordDTO {
	id: string;
	file_id: string;
	manifest_id: string;
	client_id: string | null;
	pnr_list: string[] | null;
	primary_pnr: string | null;
	passenger_name: string | null;
	passenger_first_name: string | null;
	passenger_last_name: string | null;
	passenger_email: string | null;
	flight_cross_referenced: boolean;
	outbound_departure_airport: string | null;
	outbound_arrival_airport: string | null;
	outbound_departure_date: string | null;
	outbound_arrival_date: string | null;
	outbound_flight_number: string | null;
	outbound_departure_time: string | null;
	outbound_last_leg_departure_time: string | null;
	outbound_arrival_time: string | null;
	return_departure_airport: string | null;
	return_arrival_airport: string | null;
	return_flight_date: string | null;
	return_flight_number: string | null;
	return_departure_time: string | null;
	return_arrival_time: string | null;
	all_flights?: Array<{
		departure_airport: string;
		arrival_airport: string;
		date: string;
		flight_number?: string | null;
		departure_time?: string | null;
		arrival_time?: string | null;
		direction: 'outbound' | 'return';
	}> | null;
	extraction_flags?: { multi_pnr?: boolean; unmatched?: boolean; open_jaw?: boolean; name_conflict?: boolean; attendee_created?: boolean; destination_mismatch?: boolean } | null;
	matched_attendee_id: string | null;
	confidence_score: number | null;
	is_approved: boolean;
	updates_record_id?: string | null;
}

export interface LegComparisonDTO {
	type: 'unchanged' | 'updated' | 'new' | 'existing_only';
	existing?: { departure_airport: string; arrival_airport: string; date: string; flight_number: string | null; departure_time: string | null; arrival_time: string | null; direction: string };
	incoming?: { departure_airport: string; arrival_airport: string; date: string; flight_number: string | null; departure_time: string | null; arrival_time: string | null; direction: string };
	changes?: string[];
}

export interface UpdateComparisonDTO {
	pendingRecordId: string;
	existingRecordId: string;
	passengerName: string;
	pnr: string | null;
	legs: LegComparisonDTO[];
}

export interface ManifestWithDetails extends FlightManifestDTO {
	files: FlightManifestFileDTO[];
	records: FlightManifestRecordDTO[];
}

export interface ProcessingStatusDTO {
	manifestStatus: string;
	files: Array<{ id: string; filename: string; status: string; error: string | null }>;
	summary: { total: number; uploaded: number; processing: number; extracted: number; needs_review: number; failed: number };
}

export interface EventManifestSummaryDTO {
	event_id: string;
	event_name: string;
	client_name: string;
	start_date: string | null;
	end_date: string | null;
	manifest_count: number;
	passenger_count: number;
	total_flights: number;
}

export interface RecordWithSourceDTO extends FlightManifestRecordDTO {
	source_filename: string;
	source_r2_key: string;
	manifest_name: string;
}

export interface AdvisoryDTO {
	type: string;
	severity: 'high' | 'medium' | 'low';
	message: string;
	record_ids: string[];
	metadata?: {
		existing_record_id?: string;
		new_record_id?: string;
		attendee_id?: string;
		airports?: string;
		details?: string;
	};
}

export interface EventManifestDataDTO {
	records: RecordWithSourceDTO[];
	manifests: FlightManifestDTO[];
}

export class FlightManifestService {
	async list(page = 1, pageSize = 20): Promise<{ items: FlightManifestDTO[]; total: number }> {
		const res = await apiFetch(`/api/flight-manifests?page=${page}&pageSize=${pageSize}`);
		if (!res.ok) throw new Error('Failed to load flight manifests');
		const data = await res.json();
		return data.data;
	}

	async create(eventId: string, name: string, destinationAirports?: string[]): Promise<FlightManifestDTO> {
		const res = await apiFetch('/api/flight-manifests', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ event_id: eventId, name, destination_airports: destinationAirports ?? [] }),
		});
		if (!res.ok) {
			const err = await res.json().catch(() => ({}));
			throw new Error(err.error?.message || 'Failed to create manifest');
		}
		const data = await res.json();
		return data.data;
	}

	async getById(id: string): Promise<ManifestWithDetails> {
		const res = await apiFetch(`/api/flight-manifests/${id}`);
		if (!res.ok) throw new Error('Failed to load manifest');
		const data = await res.json();
		return data.data;
	}

	async delete(id: string): Promise<void> {
		const res = await apiFetch(`/api/flight-manifests/${id}`, { method: 'DELETE' });
		if (!res.ok) throw new Error('Failed to delete manifest');
	}

	/** Upload a PDF via presigned URL (browser → R2 direct, same pattern as attachments) */
	async uploadFile(manifestId: string, file: File): Promise<FlightManifestFileDTO> {
		// 1. Get presigned PUT URL
		const presignRes = await apiFetch(`/api/flight-manifests/${manifestId}/presign`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ filename: file.name, contentType: file.type || 'application/pdf' }),
		});
		if (!presignRes.ok) throw new Error('Failed to presign upload');
		const { url, r2Key, fileId } = (await presignRes.json()).data;

		// 2. PUT file directly to R2
		const putRes = await fetch(url, {
			method: 'PUT',
			body: file,
			headers: { 'Content-Type': file.type || 'application/pdf' },
		});
		if (!putRes.ok) throw new Error(`Upload to storage failed: ${putRes.status}`);

		// 3. Finalize — record file in DB
		const finalizeRes = await apiFetch(`/api/flight-manifests/${manifestId}/finalize-upload`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ fileId, r2Key, originalFilename: file.name, size: file.size }),
		});
		if (!finalizeRes.ok) throw new Error('Failed to finalize upload');
		return (await finalizeRes.json()).data;
	}

	async process(manifestId: string): Promise<void> {
		const res = await apiFetch(`/api/flight-manifests/${manifestId}/process`, { method: 'POST' });
		if (!res.ok) throw new Error('Failed to start processing');
	}

	async getStatus(manifestId: string): Promise<ProcessingStatusDTO> {
		const res = await apiFetch(`/api/flight-manifests/${manifestId}/status`);
		if (!res.ok) throw new Error('Failed to get status');
		const data = await res.json();
		return data.data;
	}

	async updateRecord(manifestId: string, recordId: string, updates: Partial<FlightManifestRecordDTO>): Promise<void> {
		const res = await apiFetch(`/api/flight-manifests/${manifestId}/records/${recordId}`, {
			method: 'PATCH',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(updates),
		});
		if (!res.ok) throw new Error('Failed to update record');
	}

	async deleteRecord(manifestId: string, recordId: string): Promise<void> {
		const res = await apiFetch(`/api/flight-manifests/${manifestId}/records/${recordId}`, { method: 'DELETE' });
		if (!res.ok) throw new Error('Failed to delete record');
	}

	async bulkDeleteRecords(eventId: string, recordIds: string[]): Promise<{ deleted: number }> {
		const res = await apiFetch(`/api/flight-manifests/by-event/${eventId}/records/bulk-delete`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ recordIds }),
		});
		if (!res.ok) {
			const err = await res.json().catch(() => ({}));
			throw new Error(err.error?.message || 'Failed to delete records');
		}
		const data = await res.json();
		return data.data;
	}

	/** Create new attendee from record and link (for name conflict / unmatched) */
	async createAttendeeFromRecord(manifestId: string, recordId: string): Promise<{ attendee_id: string }> {
		const res = await apiFetch(`/api/flight-manifests/${manifestId}/records/${recordId}/create-attendee`, {
			method: 'POST',
		});
		if (!res.ok) {
			const err = await res.json().catch(() => ({}));
			throw new Error(err.error?.message || 'Failed to create attendee');
		}
		const data = await res.json();
		return data.data;
	}

	async approve(manifestId: string, updateMerges?: Array<{ pendingRecordId: string; existingRecordId: string; acceptedLegIndices: number[] }>): Promise<void> {
		const res = await apiFetch(`/api/flight-manifests/${manifestId}/approve`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ updateMerges: updateMerges || [] }),
		});
		if (!res.ok) {
			const err = await res.json().catch(() => ({}));
			throw new Error(err.error?.message || 'Failed to approve manifest');
		}
	}

	/** Get records for an event: approved + one specific manifest (wizard use) */
	async getAllByEvent(eventId: string, manifestId?: string): Promise<EventManifestDataDTO> {
		const params = manifestId ? `?manifestId=${manifestId}` : '';
		const res = await apiFetch(`/api/flight-manifests/by-event/${eventId}/all${params}`);
		if (!res.ok) throw new Error('Failed to load event manifests');
		const data = await res.json();
		return data.data;
	}

	async getUpdateComparisons(manifestId: string): Promise<UpdateComparisonDTO[]> {
		const res = await apiFetch(`/api/flight-manifests/${manifestId}/update-comparisons`);
		if (!res.ok) return [];
		const data = await res.json();
		return data.data;
	}

	/** LLM Call 1: Detect duplicates/matches against existing records + attendees (Review Data step) */
	async detectDuplicates(manifestId: string): Promise<AdvisoryDTO[]> {
		const res = await apiFetch(`/api/flight-manifests/${manifestId}/detect-duplicates`, { method: 'POST' });
		if (!res.ok) {
			const err = await res.json().catch(() => ({}));
			throw new Error(err.error?.message || 'Failed to detect duplicates');
		}
		const data = await res.json();
		return data.data.advisories;
	}

	/** LLM Call 2: Detect flight logic anomalies within current batch (Review & Approve step) */
	async detectAnomalies(manifestId: string): Promise<AdvisoryDTO[]> {
		const res = await apiFetch(`/api/flight-manifests/${manifestId}/detect-anomalies`, { method: 'POST' });
		if (!res.ok) {
			const err = await res.json().catch(() => ({}));
			throw new Error(err.error?.message || 'Failed to detect anomalies');
		}
		const data = await res.json();
		return data.data.advisories;
	}

	async listEventsWithManifests(): Promise<EventManifestSummaryDTO[]> {
		const res = await apiFetch('/api/flight-manifests/events');
		if (!res.ok) throw new Error('Failed to load events');
		const data = await res.json();
		return data.data;
	}

	async getByEvent(eventId: string): Promise<EventManifestDataDTO> {
		const res = await apiFetch(`/api/flight-manifests/by-event/${eventId}`);
		if (!res.ok) throw new Error('Failed to load event manifests');
		const data = await res.json();
		return data.data;
	}

	async getFileViewUrl(fileId: string): Promise<{ url: string; filename: string }> {
		const res = await apiFetch(`/api/flight-manifests/file/${fileId}/presign-view`);
		if (!res.ok) throw new Error('Failed to get file URL');
		const data = await res.json();
		return data.data;
	}

	/** Bulk lookup airport names by IATA codes */
	async lookupAirports(codes: string[]): Promise<Record<string, string>> {
		if (codes.length === 0) return {};
		const res = await apiFetch('/api/flight-manifests/airports/lookup', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ codes }),
		});
		if (!res.ok) return {};
		const data = await res.json();
		return data.data;
	}

	/** Search airports by IATA code or name */
	async searchAirports(query: string, limit = 8): Promise<Array<{ iata: string; name: string; type: string; iso_country: string | null }>> {
		const params = new URLSearchParams({ q: query, limit: String(limit) });
		const res = await apiFetch(`/api/flight-manifests/airports/search?${params}`);
		if (!res.ok) throw new Error('Failed to search airports');
		const data = await res.json();
		return data.data.airports;
	}

}
